import { AlertCircle, CalendarDays } from "lucide-react";
import { useMemo } from "react";

import { EmptyState, LoadingCards } from "components/states";

import { dayKeyOf, formatClock, formatDayChip } from "./agenda-schedule";
import { timezonePlace } from "../events/timezones";
import { NowHeader } from "./NowHeader";
import { NowLiveCard } from "./NowLiveCard";
import { NowNextRow } from "./NowNextRow";
import {
  nextSessionAfter,
  runningSessions,
  type RunningSession,
} from "./now-model";
import type { AgendaItem, Track } from "./schemas";
import { useLiveAgenda, useTracks } from "./use-event-content";
import { useNow } from "./use-now";

/**
 * The "Now" tab: what is running and what comes next (TNG-97).
 *
 * Reads the same live agenda listener as the Agenda tab and the now-and-next
 * card. Firestore's persistent cache serves it first, so on a venue's patchy
 * network the tab opens from the last agenda it saw. See `now-model.ts` for
 * what counts as running.
 */
export function NowSection({
  eventId,
  timeZone,
}: {
  eventId: string;
  /** The event's IANA zone, from the public projection, always valid. */
  timeZone: string;
}) {
  const agenda = useLiveAgenda(eventId);
  const tracks = useTracks(eventId);
  const now = useNow();
  const trackList = useMemo(
    () => (tracks.status === "success" ? tracks.data : []),
    [tracks],
  );

  if (agenda.status === "loading") return <LoadingCards cards={3} />;
  if (agenda.status === "error")
    return (
      <EmptyState icon={AlertCircle} title="Could not load the agenda">
        {agenda.error}
      </EmptyState>
    );

  return (
    <NowView
      items={agenda.data}
      tracks={trackList}
      now={now}
      timeZone={timeZone}
    />
  );
}

/** The header, the running sessions and Next at a given moment, without listeners. */
export function NowView({
  items,
  tracks,
  now,
  timeZone: zone,
}: {
  items: AgendaItem[];
  tracks: Track[];
  now: Date;
  timeZone: string;
}) {
  const running = useMemo(
    () => runningSessions(items, tracks, now),
    [items, tracks, now],
  );
  const next = useMemo(
    () => nextSessionAfter(items, tracks, now),
    [items, tracks, now],
  );
  const [featured, ...others] = running;

  return (
    <div
      className="mx-auto w-full min-w-0 max-w-2xl space-y-3"
      data-testid="now-view"
    >
      <NowHeader at={now} timeZone={zone} />
      {featured ? (
        <NowLiveCard session={featured} now={now} timeZone={zone} />
      ) : (
        <EmptyState
          icon={CalendarDays}
          title="Nothing is on right now"
          testId="now-empty"
        >
          {next
            ? `Next session ${when(next.start, now, zone)}.`
            : "Nothing else is scheduled."}
        </EmptyState>
      )}
      {others.length > 0 && (
        <section aria-label="Also on now" className="space-y-2">
          <h2 className="px-1 pt-3 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Also on now
          </h2>
          <ul className="space-y-2" data-testid="now-also">
            {others.map((session) => (
              <li key={session.item.id}>
                <AlsoOnRow session={session} timeZone={zone} />
              </li>
            ))}
          </ul>
        </section>
      )}
      {featured && next && <NowNextRow next={next} at={now} timeZone={zone} />}
      <p
        className="px-1 pt-3 text-[0.8125rem] text-muted-foreground"
        data-testid="now-clock-note"
      >
        Times are {timezonePlace(zone)} time.
      </p>
    </div>
  );
}

function AlsoOnRow({
  session,
  timeZone,
}: {
  session: RunningSession;
  timeZone: string;
}) {
  return (
    <div
      className="flex min-w-0 flex-col gap-0.5 rounded-xl border border-border bg-card px-4 py-3.5"
      data-testid={`now-also-${session.item.id}`}
    >
      <span className="text-sm font-semibold tabular-nums text-foreground">
        {formatClock(session.start, timeZone)}
        <span aria-hidden>{" – "}</span>
        <span className="sr-only"> until </span>
        {formatClock(session.end, timeZone)}
      </span>
      <span className="font-display text-lg leading-tight text-foreground wrap-anywhere">
        {session.item.title}
      </span>
      {session.room && (
        <span className="text-sm text-muted-foreground wrap-anywhere">
          {session.room}
        </span>
      )}
    </div>
  );
}

/** "at 14:00" today on the event's clock, or "Fri 11 Sep at 09:00". */
function when(start: Date, now: Date, zone: string): string {
  const clock = formatClock(start, zone);
  const day = dayKeyOf(start, zone);
  return day === dayKeyOf(now, zone)
    ? `at ${clock}`
    : `${formatDayChip(day)} at ${clock}`;
}
